import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  UserPlus,
  UserMinus,
  Upload,
  Download,
  Loader2,
} from 'lucide-react';
import {
  useLicense,
  useLicenseAllocations,
  useDeleteLicense,
  useAllocateLicense,
  useDeallocateLicense,
  useImportLicenseAllocations,
  AllocationImportError,
} from '../services/licenseService';
import { formatDate, formatCalendarDate, formatCurrency } from '../utils/formatters';
import { downloadAllocationImportTemplate } from '../utils/allocationImportTemplate';
import { AllocationType } from '../types';
import type { LicenseAllocation, AllocationImportResult, AllocationImportRowError } from '../types';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { ActiveStatusBadge, ComplianceStatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Detail row component
 *
 * Displays a label-value pair in a consistent format
 */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  // Only a missing value is a dash: 0 is a real quantity (e.g. no units allocated)
  const empty = value === undefined || value === null || value === '';
  return (
    <div className="mb-4">
      <p className="text-sm text-grey-500 mb-1">{label}</p>
      <p className="text-base font-medium">{empty ? '—' : value}</p>
    </div>
  );
}

/** Display labels for the allocation target types */
const ALLOCATION_TYPE_LABELS: Record<AllocationType, string> = {
  [AllocationType.User]: 'User',
  [AllocationType.Department]: 'Department',
  [AllocationType.CostCenter]: 'Cost Center',
};

/**
 * Add Allocation dialog state
 *
 * Mirrors the API's `CreateAllocationDto`. Quantity is kept as the typed string so the
 * field can be cleared while editing; it is validated and converted on submit.
 */
interface AllocationFormState {
  allocatedTo: string;
  allocatedToType: AllocationType;
  quantity: string;
  notes: string;
}

const EMPTY_ALLOCATION_FORM: AllocationFormState = {
  allocatedTo: '',
  allocatedToType: AllocationType.User,
  quantity: '1',
  notes: '',
};

/** Row errors listed in the import dialog before "and N more"; the API returns them all */
const MAX_IMPORT_ERRORS_SHOWN = 50;

/**
 * License Detail Page Component
 */
const LicenseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { canManageLicenses } = useAuth();
  const { id } = useParams({ strict: false }) as { id: string };

  const { data: license, isLoading, error } = useLicense(id);
  const {
    data: allocations = [],
    isLoading: allocationsLoading,
    isError: allocationsError,
  } = useLicenseAllocations(id);
  const deleteMutation = useDeleteLicense();
  const allocateMutation = useAllocateLicense();
  const deallocateMutation = useDeallocateLicense();
  const importMutation = useImportLicenseAllocations();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [allocateDialogOpen, setAllocateDialogOpen] = React.useState(false);
  const [allocationForm, setAllocationForm] =
    React.useState<AllocationFormState>(EMPTY_ALLOCATION_FORM);
  const [allocateError, setAllocateError] = React.useState<string | null>(null);
  const [deallocateTarget, setDeallocateTarget] = React.useState<LicenseAllocation | null>(null);
  const [deallocateError, setDeallocateError] = React.useState<string | null>(null);

  // Import dialog: the chosen file and option, then either the counts or the reason and row errors
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [importFile, setImportFile] = React.useState<File | null>(null);
  const [importReplace, setImportReplace] = React.useState(false);
  const [importError, setImportError] = React.useState<string | null>(null);
  const [importRowErrors, setImportRowErrors] = React.useState<AllocationImportRowError[]>([]);
  const [importResult, setImportResult] = React.useState<AllocationImportResult | null>(null);

  // Pagination for allocations DataTable
  const [allocationPagination, setAllocationPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // The table is manually paginated; after the last row of a page is deallocated, step back
  // to the last page that still has rows instead of showing an empty page.
  React.useEffect(() => {
    setAllocationPagination((current) => {
      if (current.pageIndex === 0 || current.pageIndex * current.pageSize < allocations.length) {
        return current;
      }
      const lastPage = Math.max(0, Math.ceil(allocations.length / current.pageSize) - 1);
      return { ...current, pageIndex: lastPage };
    });
  }, [allocations.length]);

  const handleBack = () => navigate({ to: '/licenses' });
  const handleEdit = () => navigate({ to: `/licenses/${id}/edit` });
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(id);
      setDeleteDialogOpen(false);
      navigate({ to: '/licenses' });
    } catch (err) {
      console.error('Failed to delete license:', err);
    }
  };

  const availableQuantity = license?.availableQuantity ?? 0;
  const requestedQuantity = Number(allocationForm.quantity);
  const quantityValid =
    allocationForm.quantity.trim() !== '' &&
    Number.isInteger(requestedQuantity) &&
    requestedQuantity >= 1 &&
    requestedQuantity <= availableQuantity;
  const allocationFormValid = allocationForm.allocatedTo.trim() !== '' && quantityValid;

  const handleAllocateClick = () => {
    setAllocationForm(EMPTY_ALLOCATION_FORM);
    setAllocateError(null);
    setAllocateDialogOpen(true);
  };

  const handleAllocateCancel = () => {
    setAllocateDialogOpen(false);
    setAllocationForm(EMPTY_ALLOCATION_FORM);
    setAllocateError(null);
  };

  const handleAllocateSubmit = async () => {
    if (!allocationFormValid) return;
    setAllocateError(null);
    try {
      await allocateMutation.mutateAsync({
        licenseId: id,
        data: {
          allocatedTo: allocationForm.allocatedTo.trim(),
          allocatedToType: allocationForm.allocatedToType,
          quantity: requestedQuantity,
          notes: allocationForm.notes.trim() || undefined,
        },
      });
      setAllocateDialogOpen(false);
      setAllocationForm(EMPTY_ALLOCATION_FORM);
    } catch (err) {
      setAllocateError(err instanceof Error ? err.message : 'Failed to allocate license');
    }
  };

  const handleDeallocateClick = (allocation: LicenseAllocation) => {
    setDeallocateError(null);
    setDeallocateTarget(allocation);
  };

  // The dialog stays open until the request settles (the action's default close is prevented
  // in onClick), so a 400 ("already deallocated") or 404 is shown instead of swallowed.
  const handleDeallocateConfirm = async () => {
    if (!deallocateTarget) return;
    setDeallocateError(null);
    try {
      await deallocateMutation.mutateAsync({ licenseId: id, allocationId: deallocateTarget.id });
      setDeallocateTarget(null);
    } catch (err) {
      setDeallocateError(err instanceof Error ? err.message : 'Failed to deallocate license');
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportReplace(false);
    setImportError(null);
    setImportRowErrors([]);
    setImportResult(null);
  };

  const handleImportClick = () => {
    resetImport();
    setImportDialogOpen(true);
  };

  // Escape, the overlay, the close icon, Cancel and Done all come through here. While the request
  // is in flight the dialog stays open: the handler below awaits it, so its outcome always lands
  // on the dialog that sent it.
  const handleImportClose = () => {
    if (importMutation.isPending) return;
    setImportDialogOpen(false);
    resetImport();
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImportError(null);
    setImportRowErrors([]);
    try {
      const result = await importMutation.mutateAsync({
        licenseId: id,
        file: importFile,
        replaceExisting: importReplace,
      });
      setImportResult(result);
    } catch (err) {
      if (err instanceof AllocationImportError) {
        setImportError(err.message);
        setImportRowErrors(err.rowErrors);
      } else {
        setImportError(err instanceof Error ? err.message : 'Import failed');
      }
    }
  };

  /**
   * Allocation columns for DataTable
   */
  const allocationColumns: ColumnDef<LicenseAllocation>[] = [
    {
      accessorKey: 'allocatedTo',
      header: 'Allocated To',
      cell: ({ row }) => <span>{row.original.allocatedTo}</span>,
    },
    {
      accessorKey: 'allocatedToType',
      header: 'Type',
      cell: ({ row }) => (
        <span>{ALLOCATION_TYPE_LABELS[row.original.allocatedToType] ?? row.original.allocatedToType}</span>
      ),
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }) => <span>{row.original.quantity}</span>,
    },
    {
      accessorKey: 'allocatedDate',
      header: 'Allocated Date',
      cell: ({ row }) => <span>{formatDate(row.original.allocatedDate)}</span>,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) =>
        canManageLicenses && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Deallocate license"
            onClick={(e) => {
              e.stopPropagation();
              handleDeallocateClick(row.original);
            }}
            disabled={!row.original.isActive || deallocateMutation.isPending}
          >
            <UserMinus className="h-4 w-4" />
          </Button>
        ),
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !license) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load license details. Please try again later.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
            Back to Licenses
          </Button>
        </div>
      </div>
    );
  }

  const utilizationPercentage = license.utilizationPercentage;

  // Client-side pagination for allocations
  const paginatedAllocations = allocations.slice(
    allocationPagination.pageIndex * allocationPagination.pageSize,
    (allocationPagination.pageIndex + 1) * allocationPagination.pageSize,
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Licenses
        </Button>

        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {license.productName}
              {license.version && (
                <span className="text-xl text-grey-500 ml-3">v{license.version}</span>
              )}
            </h1>
            <div className="flex items-center gap-2">
              <ActiveStatusBadge isActive={license.isActive} />
              <ComplianceStatusBadge status={license.complianceStatus} />
            </div>
          </div>

          {canManageLicenses && (
            <div className="flex gap-2">
              <Button onClick={handleEdit}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={handleDeleteClick}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* License Information */}
        <div className="col-span-12 md:col-span-6">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-2">License Information</h2>
              <Separator className="mb-6" />

              <DetailRow label="Contract" value={license.contractName} />
              <DetailRow label="License Type" value={license.licenseType} />
              <DetailRow
                label="Cost Per Unit"
                value={formatCurrency(license.costPerUnit, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              />
              {license.expirationDate && (
                <DetailRow label="Expiration Date" value={formatCalendarDate(license.expirationDate)} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Utilization Metrics */}
        <div className="col-span-12 md:col-span-6">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-2">Utilization Metrics</h2>
              <Separator className="mb-6" />

              <DetailRow label="Total Licenses" value={license.totalQuantity} />
              <DetailRow label="Allocated Licenses" value={license.allocatedQuantity} />
              <DetailRow
                label="Available Licenses"
                value={
                  <span
                    className={cn(
                      'text-base font-medium',
                      license.availableQuantity > 0 ? 'text-success' : 'text-destructive',
                    )}
                  >
                    {license.availableQuantity}
                  </span>
                }
              />

              <div className="mt-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-grey-500">Utilization</span>
                  <span className="text-sm font-medium">{utilizationPercentage.toFixed(0)}%</span>
                </div>
                <Progress
                  value={Math.min(utilizationPercentage, 100)}
                  className="h-2"
                  indicatorClassName={cn(
                    utilizationPercentage >= 90
                      ? 'bg-destructive'
                      : utilizationPercentage >= 80
                        ? 'bg-warning'
                        : 'bg-success',
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* License Key */}
        {license.licenseKey && (
          <div className="col-span-12">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-2">License Key</h2>
                <Separator className="mb-6" />
                <p className="font-mono bg-grey-50 p-4 rounded-md">{license.licenseKey}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notes */}
        {license.notes && (
          <div className="col-span-12">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-2">Notes</h2>
                <Separator className="mb-6" />
                <p className="whitespace-pre-wrap">{license.notes}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Allocations */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  License Allocations ({allocations.length})
                </h2>
                {canManageLicenses && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleImportClick}
                      // Not gated on availability: Enterprise and Site licenses have no cap and
                      // replace mode frees units before the capacity check
                      disabled={allocationsLoading}
                    >
                      <Upload className="h-4 w-4" />
                      Import
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAllocateClick}
                      // Also wait for the first allocations fetch: an invalidation issued while
                      // that request is still in flight reuses it (no data cached yet), so an
                      // allocation created in that window would not appear until the next refetch
                      disabled={availableQuantity <= 0 || allocationsLoading}
                    >
                      <UserPlus className="h-4 w-4" />
                      Add Allocation
                    </Button>
                  </div>
                )}
              </div>
              <Separator className="mb-6" />

              {allocationsError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>Failed to load allocations. Please try again later.</AlertDescription>
                </Alert>
              )}

              <DataTable
                columns={allocationColumns}
                data={paginatedAllocations}
                rowCount={allocations.length}
                isLoading={allocationsLoading}
                pagination={allocationPagination}
                onPaginationChange={setAllocationPagination}
                emptyMessage="No allocations for this license"
                pageSizeOptions={[5, 10, 25]}
              />
            </CardContent>
          </Card>
        </div>

        {/* Metadata */}
        <div className="col-span-12">
          <Card className="bg-grey-50">
            <CardContent className="p-6">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-6">
                  <p className="text-sm text-grey-500">Created</p>
                  <p className="text-sm">{formatDate(license.createdAt, 'PPpp')}</p>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <p className="text-sm text-grey-500">Last Updated</p>
                  <p className="text-sm">{formatDate(license.updatedAt, 'PPpp')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete License</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this license? This action cannot be undone. A
              license with active allocations cannot be deleted; deallocate them first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deallocate Confirmation Dialog */}
      <AlertDialog
        open={deallocateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeallocateTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deallocate License</AlertDialogTitle>
            <AlertDialogDescription>
              {deallocateTarget
                ? `Return the ${deallocateTarget.quantity} unit${deallocateTarget.quantity === 1 ? '' : 's'} allocated to ${deallocateTarget.allocatedTo} to the pool?`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deallocateError && (
            <Alert variant="destructive">
              <AlertDescription>{deallocateError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deallocateMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeallocateConfirm();
              }}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deallocateMutation.isPending}
            >
              {deallocateMutation.isPending ? 'Deallocating...' : 'Deallocate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Allocation Dialog */}
      <Dialog
        open={allocateDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleAllocateCancel();
          else setAllocateDialogOpen(true);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add License Allocation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {allocateError && (
              <Alert variant="destructive">
                <AlertDescription>{allocateError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="allocation-allocated-to">Allocate To *</Label>
              <Input
                id="allocation-allocated-to"
                placeholder="Email, department or cost center"
                maxLength={200}
                value={allocationForm.allocatedTo}
                onChange={(e) =>
                  setAllocationForm({ ...allocationForm, allocatedTo: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="allocation-type">Type *</Label>
              <Select
                value={allocationForm.allocatedToType}
                onValueChange={(value) =>
                  setAllocationForm({ ...allocationForm, allocatedToType: value as AllocationType })
                }
              >
                <SelectTrigger id="allocation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(AllocationType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {ALLOCATION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="allocation-quantity">Quantity *</Label>
              <Input
                id="allocation-quantity"
                type="number"
                min={1}
                max={availableQuantity}
                step={1}
                value={allocationForm.quantity}
                onChange={(e) => setAllocationForm({ ...allocationForm, quantity: e.target.value })}
              />
              <p className="text-xs text-grey-500">
                {availableQuantity} available
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="allocation-notes">Notes</Label>
              <Textarea
                id="allocation-notes"
                maxLength={2000}
                value={allocationForm.notes}
                onChange={(e) => setAllocationForm({ ...allocationForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleAllocateCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleAllocateSubmit}
              disabled={allocateMutation.isPending || !allocationFormValid}
            >
              {allocateMutation.isPending ? 'Allocating...' : 'Allocate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Allocations Dialog */}
      <Dialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleImportClose();
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Allocations</DialogTitle>
          </DialogHeader>
          {importResult ? (
            <div className="space-y-2 mt-2">
              <p className="font-semibold">Import complete</p>
              <p>{importResult.created} created</p>
              <p>{importResult.skipped} skipped (already allocated)</p>
              <p>{importResult.deallocated} deallocated</p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {importError && (
                <Alert variant="destructive">
                  <AlertDescription>{importError}</AlertDescription>
                </Alert>
              )}
              {importRowErrors.length > 0 && (
                <div className="rounded-md border border-grey-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-grey-50 text-left">
                      <tr>
                        <th className="px-3 py-2 font-medium w-16">Row</th>
                        <th className="px-3 py-2 font-medium">Problem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRowErrors.slice(0, MAX_IMPORT_ERRORS_SHOWN).map((error) => (
                        <tr key={`${error.row}-${error.message}`} className="border-t border-grey-200">
                          <td className="px-3 py-1.5 tabular-nums">{error.row}</td>
                          <td className="px-3 py-1.5">{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importRowErrors.length > MAX_IMPORT_ERRORS_SHOWN && (
                    <p className="px-3 py-2 text-xs text-grey-500 border-t border-grey-200">
                      and {importRowErrors.length - MAX_IMPORT_ERRORS_SHOWN} more
                    </p>
                  )}
                </div>
              )}
              <div>
                <Button
                  variant="outline"
                  className="w-full py-6"
                  onClick={() => document.getElementById('allocation-import-file')?.click()}
                  disabled={importMutation.isPending}
                >
                  <Upload className="h-4 w-4" />
                  {importFile ? importFile.name : 'Choose File'}
                </Button>
                <input
                  id="allocation-import-file"
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={(e) => {
                    setImportFile(e.target.files?.[0] ?? null);
                    // Let the same file be chosen again after a failed attempt
                    e.target.value = '';
                  }}
                />
                <p className="text-xs text-grey-500 mt-2">
                  CSV with columns allocatedTo (or email), allocatedToType, quantity, notes. Max 5MB.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 px-0"
                  onClick={downloadAllocationImportTemplate}
                >
                  <Download className="h-4 w-4" />
                  Download template
                </Button>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="allocation-import-replace"
                  className="mt-0.5"
                  checked={importReplace}
                  onCheckedChange={(checked) => setImportReplace(checked === true)}
                  disabled={importMutation.isPending}
                />
                <div className="space-y-1">
                  <Label htmlFor="allocation-import-replace">Replace existing user allocations</Label>
                  <p className="text-xs text-grey-500">
                    User allocations not in the file are deallocated. Department and cost center
                    allocations are kept.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {importResult ? (
              <Button onClick={handleImportClose}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleImportClose} disabled={importMutation.isPending}>
                  Cancel
                </Button>
                <Button onClick={handleImportSubmit} disabled={importMutation.isPending || !importFile}>
                  {importMutation.isPending ? 'Importing...' : 'Import'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicenseDetail;
